import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import CalendarIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/calendar';
import ChartIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/chart';
import {
  BpkBox,
  BpkSpacing,
  BpkStack,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { BpkSegmentedControlV2 } from '@skyscanner-internal/backpack-web/bpk-component-segmented-control';
import { useDispatch, useSelector } from 'react-redux';

import { setFlightViewMode } from '../../actions/ui-actions';
import {
  CALENDAR_VIEW_MODE,
  BAR_CHART_VIEW_MODE,
} from '../../helpers/constants/dataTypeConstants';
import { useMediaQuery } from '../../helpers/hooks/useMediaQuery';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { trackInteractionEvent } from '../../helpers/logging/trackInteractionEvent';
import { trackViewModeSmartMetric } from '../../middleware/trackEvent';
import { getSelectedViewMode } from '../../selectors';

import type { State } from '../../selectors/types';

type Tab = {
  id: string;
  title: string | undefined;
  icon: React.ComponentType<{ className?: string }>;
};

type CalendarChartSwitchProps = {
  children: React.ReactNode[];
};

const VIEW_MODE_CONTENT_ID = 'view-mode-content';

const CalendarChartSwitch = ({ children }: CalendarChartSwitchProps) => {
  const dispatch = useDispatch();
  const translate = useTranslation();
  const selectedTab = useSelector((state: State) => getSelectedViewMode(state));
  const isMobilePhone = useMediaQuery(BREAKPOINTS.MOBILE);
  const effectiveTab = isMobilePhone ? CALENDAR_VIEW_MODE : selectedTab;
  const calendarCardLabel = translate('calendar_card_label');
  const chartCardLabel = translate('chart_card_label');

  const tabs: Tab[] = [
    {
      id: CALENDAR_VIEW_MODE,
      title: calendarCardLabel,
      icon: CalendarIcon,
    },
    {
      id: BAR_CHART_VIEW_MODE,
      title: chartCardLabel,
      icon: ChartIcon,
    },
  ];

  const onTabSelect = (tabId: string) => {
    dispatch(setFlightViewMode(tabId));
    trackViewModeSmartMetric(tabId);
    trackInteractionEvent({
      name: 'View_Mode_Switched',
    });
  };

  return (
    <BpkBox position="relative">
      {!isMobilePhone && (
        <BpkBox
          position="absolute"
          insetInlineEnd={BpkSpacing.None}
          paddingInline={BpkSpacing.LG}
        >
          <BpkSegmentedControlV2.Root
            label={`${calendarCardLabel} / ${chartCardLabel}`}
            value={effectiveTab}
            onChange={onTabSelect}
          >
            {tabs.map(({ icon: Icon, id, title }) => (
              <BpkSegmentedControlV2.Item key={id} value={id}>
                <BpkSegmentedControlV2.ItemText>
                  <BpkVessel as="span" data-percy-id={`switch-tab-${id}`}>
                    <BpkStack
                      as="span"
                      direction="row"
                      align="center"
                      gap={BpkSpacing.SM}
                    >
                      <Icon />
                      {title || ''}
                    </BpkStack>
                  </BpkVessel>
                </BpkSegmentedControlV2.ItemText>
                <BpkSegmentedControlV2.ItemControl />
                <BpkSegmentedControlV2.ItemHiddenInput />
              </BpkSegmentedControlV2.Item>
            ))}
            <BpkSegmentedControlV2.Indicator />
          </BpkSegmentedControlV2.Root>
        </BpkBox>
      )}
      {children.map(
        (child, index) =>
          effectiveTab === tabs[index].id && (
            <BpkBox key={tabs[index].id} id={VIEW_MODE_CONTENT_ID}>
              {child}
            </BpkBox>
          ),
      )}
    </BpkBox>
  );
};

export default CalendarChartSwitch;
